import dayjs, { Dayjs } from "dayjs";
import { LoaderCircle } from "lucide-react";
import * as XLSX from "xlsx";
import { message } from "antd";
import { useEffect, useState } from "react";
import { getRequest } from "./ApiCalls";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

declare module "jspdf" {
  interface jsPDF {
    lastAutoTable: {
      finalY?: number;
      [key: string]: any;
    };
  }
}

export const version = "Version 1.0.0";
export const versionShort = "V1.0.0";

export type DateRangeType = "today" | "week" | "month" | "six_months" | "year";

export function getDateRange(rangeType: DateRangeType): [Dayjs, Dayjs] {
  const today = dayjs();

  switch (rangeType) {
    case "today":
      return [today.startOf("day"), today.endOf("day")];

    case "week":
      return [today.startOf("week"), today.endOf("week")];

    case "month":
      return [today.startOf("month"), today.endOf("month")];

    case "six_months":
      return [
        today.subtract(6, "month").startOf("month"),
        today.endOf("month"),
      ];

    case "year":
      return [today.startOf("year"), today.endOf("year")];

    default:
      throw new Error(`Unsupported range type: ${rangeType}`);
  }
}

type ExcelData = {
  [key: string]: string; // dynamic key-value pair for any columns
};

export const emptySpaceErr = "Only spaces are not allowed";

// var role = getUserRole();

export const getToken = () => {
  const data = localStorage.getItem("token");
  if (data) return data;
  else return null;
};

export function getUserRole() {
  return localStorage.getItem("role");
}

export function getUserBranch() {
  return localStorage.getItem("branch_id");
}

export const getRequester = () => {
  const data = localStorage.getItem("id");
  if (data) return data;
  else return null;
};

export const navigateTo = (link: string) => {
  window.location.href = link;
};

export function refreshPage() {
  window.location.reload();
}

export const openNewTab = (link: any) => {
  if (link) {
    // Open the link in a new tab
    window.open(link, "_blank", "noopener,noreferrer");
  } else {
    console.error("No link provided");
  }
};

export const requiredBadge = () => {
  return <span className="text-red-500">*</span>;
};

export function LoaderProgress(size: number = 30, stroke: number = 2) {
  return (
    <LoaderCircle
      className="spinner text-pageBg"
      size={size}
      strokeWidth={stroke}
    />
  );
}

export function getAccountName(section: string) {
  return localStorage.getItem("full_name") ?? "";
}

export function getSum(numbers: any) {
  return numbers.reduce((acc: any, num: any) => acc + num, 0);
}

export function formatString(str: any) {
  if (str) {
    return str
      .split("_") // Split the string by underscores
      .map(
        (word: any) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() // Capitalize first letter, lowercase the rest
      )
      .join(" "); // Join the words with spaces
  }
}

export function formatStrings(
  str: string,
  format: string = "capitalize"
): string | number | null {
  if (!str) {
    return null; // Return null for invalid input
  }

  // Remove underscores and normalize spacing
  str = str.replace(/_/g, " ").trim();

  // Utility function to check if a string is a valid Roman numeral
  const isRomanNumeral = (input: string): boolean => {
    const romanRegex =
      /^(?=[MDCLXVI])M*(C[MD]|D?C{0,3})(X[CL]|L?X{0,3})(I[XV]|V?I{0,3})$/i;
    return romanRegex.test(input);
  };

  // Utility function to convert Roman numerals to numeric values
  const romanToInt = (roman: string): number => {
    const romanMap: Record<string, number> = {
      I: 1,
      V: 5,
      X: 10,
      L: 50,
      C: 100,
      D: 500,
      M: 1000,
    };
    let total = 0;
    let prevValue = 0;

    for (let i = roman.length - 1; i >= 0; i--) {
      const current = romanMap[roman[i].toUpperCase()];
      if (current < prevValue) {
        total -= current; // Subtract if smaller than the previous value (e.g., IV = 4)
      } else {
        total += current; // Add otherwise
      }
      prevValue = current;
    }

    return total;
  };

  // Handle the specified format
  switch (format.toLowerCase()) {
    case "uppercase":
      return str.toUpperCase();
    case "lowercase":
      return str.toLowerCase();
    case "capitalize":
      return str
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    case "roman":
      if (isRomanNumeral(str)) {
        return romanToInt(str);
      }
      return `Invalid Roman numeral: ${str}`;
    default:
      return `Unsupported format: ${format}`;
  }
}

export function handleOnWheel(e: any) {
  if (e) {
    return e.target.blur();
  }
}

export function handleKeyPress(e: any, restriction: string) {
  if (e) {
    if (restriction === "-") {
      if (e.key === "-") {
        e.preventDefault();
      }
    } else if (restriction === "arrow") {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
      }
    } else {
      if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "-") {
        e.preventDefault();
      }
    }
  }
}

export function handleChangePage(
  current: any,
  setFilterConfig: React.Dispatch<React.SetStateAction<any>>
) {
  setFilterConfig((prev: any) => ({
    ...prev,
    page: current,
  }));
}

export const getColumnSum = (data: any[], columnKey: string): number => {
  if (!Array.isArray(data) || data.length === 0) return 0.0;
  const total = data.reduce((sum, row) => {
    const value = parseFloat(row[columnKey]) || 0;
    return sum + value;
  }, 0);
  return total;
};

export const handlePageSizeChange = (size: number, setFilterConfig: any) => {
  setFilterConfig((prev: any) => ({ ...prev, page: 1, page_size: size }));
};

export const formatDate = (date: any, format: string): string | null => {
  if (typeof date === "number") {
    const excelEpoch = 25569;
    const msPerDay = 86400 * 1000;

    const jsDate = new Date((date - excelEpoch) * msPerDay);

    return dayjs(jsDate).format(format);
  }

  if (typeof date === "string") {
    return dayjs(date).format(format);
  }

  if (date instanceof Date) {
    return dayjs(date).format(format);
  }

  const parsedDate = dayjs(date, "DD/MM/YYYY HH:mm:ss", true);

  if (parsedDate.isValid()) {
    return parsedDate.format(format);
  }

  return null;
};

export function formatTime(time: any) {
  return dayjs(time, "HH:mm").format("h:mm A"); // Converts 24-hour format to 12-hour with AM/PM
}

export function formatNumbers(num: any) {
  if (!isNaN(num)) {
    return Number(num).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return "0.00"; // Default return if num is invalid
}

export function formatNumber(num: any) {
  if (!isNaN(num)) {
  //   let parts = num?.toString()?.split(".");
  //   parts[0] = parts[0]?.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  //   return parts.join(".");
    return Number(num).toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}

export function handleExportToExcel(
  data: any[],
  columns: any[],
  fileName: string
) {
  if (!data || data?.length === 0 || !columns || columns?.length === 0) {
    message.warning("No data or columns provided for export.");
    return;
  }

  // Extract column headers
  const headers = columns.map((col) => col.dataIndex);
  const headerLabels = columns.map((col) => col.title);

  // Convert data to a format suitable for Excel
  const sheetData = [
    headerLabels, // First row: column titles
    ...data.map((row) =>
      headers.map((key, index) => {
        const column = columns[index];
        // If column has a render function, apply it and extract the plain text
        if (column.render) {
          return typeof column.render === "function"
            ? column.render(row[key], row)?.props?.children ?? "" // Call the function // Extract text content (for <span>)
            : row[key] ?? "";
        }
        return row[key] ?? "";
      })
    ),
  ];

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  // Auto-adjust column widths based on content length
  const columnWidths = headers.map((col, index) => ({
    wch: Math.max(
      headerLabels[index].length,
      ...data.map((row) => (row[col] ? row[col].toString().length : 0))
    ),
  }));
  worksheet["!cols"] = columnWidths;

  // Create and save workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

export function handleExportToExcelWithFilters(
  data: any[],
  columns: any[],
  fileName: string,
  filters?: Record<string, any>
) {
  if (!data || data?.length === 0 || !columns || columns?.length === 0) {
    message.warning("No data or columns provided for export.");
    return;
  }

  const headers = columns.map((col) => col.dataIndex);
  const headerLabels = columns.map((col) => col.title);

  // Convert data rows
  const dataRows = data.map((row) =>
    headers.map((key, index) => {
      const column = columns[index];
      if (column.render && typeof column.render === "function") {
        return column.render(row[key], row)?.props?.children ?? "";
      }
      return row[key] ?? "";
    })
  );

  // Convert filters to row format (e.g. "Branch ID: 123")
  const filterRows: any[][] = filters
    ? Object.entries(filters).map(([key, value]) => [
        key.replace(/_/g, " ").toUpperCase(),
        value || "-",
      ])
    : [];

  if (filterRows.length > 0) {
    filterRows.push([]); // Empty row separator
  }

  // Combine all together
  const sheetData = [...filterRows, headerLabels, ...dataRows];

  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  const columnWidths = headers.map((col, index) => ({
    wch: Math.max(
      headerLabels[index]?.length ?? 10,
      ...data.map((row) => (row[col] ? row[col].toString().length : 0))
    ),
  }));
  worksheet["!cols"] = columnWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

export function handleExportToPDF(
  data: any[],
  columns: any[],
  fileName: string,
  headerInfo?: { title: string; content: string },
  footerText?: string
) {
  if (!data?.length || !columns?.length) {
    console.warn("No data or columns for PDF export.");
    return;
  }

  const doc = new jsPDF();

  // Header
  if (headerInfo) {
    doc.setFontSize(18);
    doc.text(headerInfo.title, 14, 22);
    doc.setFontSize(10);
    const pageWidth = doc.internal.pageSize.getWidth();
    const wrapped = doc.splitTextToSize(headerInfo.content, pageWidth - 35);
    doc.text(wrapped, 14, 30);
  }

  // Prepare table
  autoTable(doc, {
    head: [columns.map((col) => col.title)],
    body: data.map((row) =>
      columns.map((col) => {
        const val = row[col.dataIndex];
        return typeof val === "string" || typeof val === "number" ? val : "";
      })
    ),
    startY: 40,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [79, 104, 141] },
    theme: "grid",
  });

  // Footer
  if (footerText) {
    const lastY = doc.lastAutoTable.finalY || 50;
    doc.text(footerText, 14, lastY + 10);
  }

  doc.save(fileName);
}

export function handleExportToPDFWithFilters(
  data: any[],
  columns: any[],
  fileName: string,
  headerInfo?: { title: string; content: string },
  footerText?: string
) {
  if (!data?.length || !columns?.length) {
    console.warn("No data or columns for PDF export.");
    return;
  }

  const doc = new jsPDF();
  let currentY = 22;

  // Header
  if (headerInfo) {
    doc.setFontSize(18);
    doc.text(headerInfo.title, 14, currentY);
    currentY += 8;

    doc.setFontSize(10);
    const lines = headerInfo.content.split("\n");

    lines.forEach((line) => {
      const [label, value] = line.split(": ");
      if (label && value !== undefined) {
        doc.setFont("helvetica", "normal");
        doc.text(`${label}:`, 14, currentY);
        doc.setFont("helvetica", "normal");
        doc.text(` ${value}`, doc.getTextWidth(`${label}:`) + 14, currentY);
      } else {
        doc.setFont("helvetica", "normal");
        doc.text(line, 14, currentY);
      }
      currentY += 6; // vertical spacing between lines
    });

    doc.setFont("helvetica", "normal"); // reset
  }

  // Prepare table below header block
  autoTable(doc, {
    head: [columns.map((col) => col.title)],
    body: data.map((row) =>
      columns.map((col) => {
        const val = row[col.dataIndex];
        return typeof val === "string" || typeof val === "number" ? val : "";
      })
    ),
    startY: currentY + 4, // dynamically set Y position below header
    styles: { fontSize: 8 },
    headStyles: { fillColor: [79, 104, 141] },
    theme: "grid",
  });

  // Footer
  if (footerText) {
    const lastY = doc.lastAutoTable.finalY || currentY + 50;
    doc.text(footerText, 14, lastY + 10);
  }

  doc.save(fileName);
}

const validateExcelHeaders = (headers: string[], expectedHeaders: string[]) => {
  const missingHeaders = expectedHeaders.filter(
    (header) => !headers.includes(header)
  );
  return missingHeaders;
};

export const processExcelFile = (
  file: File,
  expectedHeaders: any[],
  onDataProcessed: (data: ExcelData[]) => void
) => {
  if (file) {
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = e.target?.result;
      const workbook = XLSX.read(data, { type: "binary" });

      // Assuming we're working with the first sheet
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      // Convert sheet data to JSON
      const sheetData = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
      }) as string[][];

      // Extract headers
      const headers = sheetData[0];
      const missingHeaders = validateExcelHeaders(
        headers,
        expectedHeaders.map((header: any) => header.label)
      );

      if (missingHeaders.length > 0) {
        message.warning(
          `Invalid file format! Missing headers: ${missingHeaders.join(", ")}`
        );
        return;
      }

      // Extract and format data rows dynamically based on the expected headers
      const dataRows = sheetData.slice(1).map((row) => {
        const rowData: ExcelData = {};

        expectedHeaders.forEach((header, index) => {
          const headerIndex = headers.indexOf(header.label);
          if (headerIndex !== -1) {
            const cellValue = row[headerIndex];

            if (header.type === "date") {
              const formattedDate = formatDate(
                cellValue,
                "YYYY-MM-DD HH:mm:ss"
              );
              // const formattedDate = cellValue;
              if (!formattedDate) {
                message.warning(
                  `Invalid date format in column: ${header.label}`
                );
              }
              rowData[header.value] = formattedDate || cellValue;
            } else {
              rowData[header.value] = cellValue;
            }
          }
        });
        return rowData;
      });

      // Call the callback with the processed data
      onDataProcessed(dataRows);
    };

    // Read the file as binary string
    reader.readAsBinaryString(file);
  }
};

export const GetAllShips = (withAll: boolean = false) => {
  const [data, setData]: any = useState([]);
  const defaultVal = {
    label: "All Ships",
    value: "",
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getRequest(`ships/get`, {
          status: "active",
        });

        if (response.success) {
          var data = response?.data.data?.map((details: any) => {
            return {
              value: details.id,
              label: details.name,
            };
          });

          if (withAll) {
            setData([defaultVal, ...data]);
          } else {
            setData([...data]);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    fetchData();
  }, []);

  return data;
};

export const useGetUserRoles = (withAll: boolean = false) => {
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getRequest(`roles/get`);
        if (response.success) {
          const rolesData = response?.data?.data?.map((details: any) => ({
            value: details.id,
            label: formatStrings(details.name, "capitalize"),
          }));

          if (withAll) {
            setData([{ label: "All Roles", value: "" }, ...rolesData]);
          } else {
            setData(rolesData);
          }
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    }

    fetchData();
  }, [withAll]);

  return data;
};

export const GetAllCompanies = (withAll: boolean = false) => {
  const [data, setData]: any = useState([]);
  const defaultVal = {
    label: "All Companies",
    value: "",
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getRequest(`companies/get`);

        if (response.success) {
          var data = response?.data.data?.map((details: any) => {
            return {
              value: details.id,
              label: details.name,
            };
          });

          if (withAll) {
            setData([defaultVal, ...data]);
          } else {
            setData([...data]);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    fetchData();
  }, []);

  return data;
};

export const GetAllRoutes = (withAll: boolean = false) => {
  const [data, setData]: any = useState([]);
  const defaultVal = {
    label: "All Routes",
    value: "",
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getRequest(`routes/get`, {
          status: "active",
        });

        if (response.success) {
          var data = response?.data.data?.map((details: any) => {
            return {
              value: details.id,
              label: `${details.departure_port} to ${details.destination_port}`,
            };
          });
          if (withAll) {
            setData([defaultVal, ...data]);
          } else {
            setData([...data]);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    fetchData();
  }, []);

  return data;
};

export const GetAllAccomodations = (withAll: boolean = false) => {
  const defaultVal = {
    label: "All Accommodations",
    value: "",
  };
  const [data, setData]: any = useState([]);

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getRequest(`accommodations/get`, {
          status: "active",
        });

        if (response.success) {
          var data = response?.data.data?.map((details: any) => {
            return {
              ...details,
              value: details.id,
              label: details.name,
            };
          });
          if (withAll) {
            setData([defaultVal, ...data]);
          } else {
            setData([...data]);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    fetchData();
  }, []);

  return data;
};

export const GetAllSchedules = (withAll: boolean = false) => {
  const [data, setData]: any = useState([]);
  const defaultVal = {
    label: "All Schedules",
    value: "",
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getRequest(`schedules/get`, {
          status: "active",
        });

        if (response.success) {
          var data = response?.data.data?.map((sched: any) => {
            return {
              value: sched.id,
              label: `${formatDate(
                sched.departure_date,
                "MMM DD, YYYY hh:mm A"
              )} - ${formatDate(sched.arrival_date, "MMM DD, YYYY hh:mm A")}`,
            };
          });
          if (withAll) {
            setData([defaultVal, ...data]);
          } else {
            setData([...data]);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    fetchData();
  }, []);

  return data;
};

export function reloadPage() {
  window.location.reload(); // force reload from server
}

//VARIABLES
export const accommodationTypes = [
  {
    label: "Economy",
    value: "economy",
  },
  {
    label: "Tourist",
    value: "tourist",
  },
  {
    label: "Cabin",
    value: "cabin",
  },
  {
    label: "Sitting",
    value: "sitting",
  },
];

export function resetPagination(filterConfig: any) {
  return {
    ...filterConfig,
    page: 1,
  };
}
